import { CSSProperties } from 'react'
import { Unpacked } from '../../type/util'

const colorGray = (alpha: number) => {
  const colorGray100 = '#0c3842'
  return `${colorGray100}${Math.round(255 * alpha).toString(16)}`
}

const colorPrimary = (alpha: number) => {
  const colorPrimary100 = '#DE8900'
  return `${colorPrimary100}${Math.round(255 * alpha).toString(16)}`
}

const colorAccent2nd = (alpha: number) => {
  const colorAccent2nd100 = '#eb9a3c'
  return `${colorAccent2nd100}${Math.round(255 * alpha).toString(16)}`
}

export const color = {
  colorGray100: colorGray(1.0),
  colorGray80: colorGray(0.8),
  colorGray60: '#666',
  colorGray40: colorGray(0.4),
  colorGray30: colorGray(0.3),
  colorGray20: colorGray(0.2),
  colorGray10: colorGray(0.1),
  colorGray5: colorGray(0.05),
  colorText: colorGray(1.0),
  colorBlack: '#000',
  colorSuccess: '#37c01a',
  colorBackground: '#F9F9F9',
  colorPrimary100: colorPrimary(1.0),
  colorPrimary30: colorPrimary(0.3),
  colorPrimary10: colorPrimary(0.1),
  colorPrimaryLight: '#65c1d5',
  colorSecondary: '#f09598',
  colorAlert: '#eb141c',
  colorAccent1: '#edd828',
  colorAccent2nd: colorAccent2nd(1.0),
  colorAccent2nd10: colorAccent2nd(0.1),
  colorAccent3: '#656ebb',
  colorAccent4: '#15A4D4',
  colorBrand: '#EB2D22',
  colorCaution: '#AE000E',
  colorLightGray: '#AAAAAA',
  colorLightGray3: '#EDECEB',
  colorGray: '#7F7D76',
  colorAzklRed: '#EB2D23',
  colorAzklRedDark: '#BF1C13',
  colorFukidashiBgRed: '#fbd5d3',
  colorAzklPink: '#ED7788',
  colorHoliday: '#E83068',
  colorBlue: '#17AECD',
  colorYellow: '#FFFB06',
  colorDarkGray: '#666',
  colorAzklPaleYellow: '#FFE8B6',
  colorWhite: '#FFFFFF',
  transparent: 'transparent',
  colorVacancies: '#0cb300',
  colorVacanciesLittle: '#de8900',
  colorCongestion: '#d93a29',
  colorFull: '#a80046',
  colorMonochromeGray: '#9f9e98',
  colorErrorRed: '#3b5a63',
  colorUltraDarkGray: '#3F3D35',
  colorChangeLogsDialogTabsContainerBg: '#F2F2F2',
  colorChildIconBg: '#e6eaeb',
  colorQrcodeBorder: '#C9C9C6',
  bgLineLink: '#1BB91F',
  bgBarcodeLink: '#17ADCE',
  colorLightBeige: '#FFF3D9',
  colorBjAzRed: '#D92F00',
  colorStatusPink: '#F07D80',
  colorAlertSuccess: '#0CB200',
  colorScTextSub: '#3B5A63',
  colorScTextBrownHeading: '#473423',
  colorScTextHeading: '#26393F',
  colorScGrayBeige: '#EBE7E2',
  colorScSeparator: '#C4DDE4',
  colorScSeparatorBrown: '#C3BDB6',
  colorMonochromeLightGray: '#B3B3B3',
  colorBlueText: '#004B6B',
  colorBlueBorder: '#68c0ce',
  colorBlueFill: '#daf8f8',
  colorScTextBox: '#7AA5B1',
  colorScbgEggshell: '#F9FCFC',
  colorScGrayish: '#F2F6F7',
  colorCityContractStatusSuccess: '#eaffe9',
  colorScLink: '#0177A9',
  colorScTextSub2: '#7a6957',
  colorScTextHedding2: '#473423',
  colorScDisabled: '#D0D0D0',
  colorScTextBrownSub: '#7A6957',
  colorBlueCheck: '#3CBBCE',
  colorPink: '#FFDBD4',
  colorScBackgroundAccent: '#E6F2F5',
  colorScPlaceholder: '#757575',
  colorBjTextSub: '#3b5a63',
  colorScRedWhitih: '#FFF7F5',
  colorScTextMain2: '#211000',
  colorScAzRedWhitish: '#FFF7F5',
  colorHolidayLabel: '#d43a71',
} as const

export type Color = Unpacked<typeof color>

export const flexColumn: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
}

export const flexRow: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
}

export const contentPadding = {
  normal: '25px',
}

export const borderRadius = {
  base: '15px',
  small: '10px',
  mini: '4px',
  none: '0',
} as const

export type BorderRadius = Unpacked<typeof borderRadius>

export const fontSize = {
  base: '1rem', // 16px
  _10: `${10 / 16}rem`,
  _12: `${12 / 16}rem`,
  _14: `${14 / 16}rem`,
  _15: `${15 / 16}rem`,
  _16: `1rem`,
  _18: `${18 / 16}rem`,
  _20: `${20 / 16}rem`,
  _24: `${24 / 16}rem`,
} as const

export type FontSize = Unpacked<typeof fontSize>

export const lineHeight = {
  base: 2,
  half: 1.5,
  short: 1.25,
  none: 1,
} as const

export type LineHeight = Unpacked<typeof lineHeight>

export const boxShadow = {
  base: '0px 3px 8px rgba(0, 0, 0, 0.08)',
  popUp: '0px 3px 15px rgba(0, 0, 0, 0.2)',
} as const

export type BoxShadow = Unpacked<typeof boxShadow>

export const breakPoint = {
  maxWidth319: '(max-width: 319px)',
  maxWidth480: '(max-width: 480px)',
  maxWidth767: '(max-width: 767px)',
  maxWidth1280: '(max-width: 1280px)',
}

export type BreakPoint = Unpacked<typeof breakPoint>

// 問診のヘッダーの高さは 60px
export const reservationFormHeaderHeight = 60
